// src/contexts/AuthContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Auth, type AuthConfig } from "@next-social-platform/chatbot_sso";
import { useNavigate } from "react-router";
import { createLogger } from "../utils/logger";
import { config } from "../config";
import { accessFromUser, can as canAccess, roleIn, type Access, type Capability, type EffectiveRole } from "../lib/tenantAccess";
import { withTenantHeader } from "../lib/tenantHeader";
import i18n from "../i18n";
import { useTranslation } from 'react-i18next';

const authLog = createLogger('Auth');

type AuthSDK = Auth;

const SSO_ORIGIN =
  (import.meta.env.VITE_SSO_ORIGIN as string | undefined) ??
  'https://sso.chatbot.mn';
const OIDC_CLIENT_ID =
  (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined) ??
  'chatbot_live';

interface IAuthContext {
  // RAW PROPERTIES (For Guards Only - Direct Access)

  // OAuth authentication (operators/admins)
  oauthUser: any;
  oauthToken: string | null;
  authenticated: boolean;

  // HIGH-LEVEL PROPERTIES (For Components - Convenience)
  user: any;                        // Returns oauthUser if authenticated
  token: string | null;             // Returns oauthToken if authenticated (may be cached)
  isOAuth: boolean;                 // True if user is OAuth authenticated
  tenants?: Array<{ id: string; name: string; slug: string }>; // User's tenants from SSO

  // TOKEN FUNCTION (BEST PRACTICE - Always gets fresh token from SDK)
  getToken: () => string | null;    // Always gets fresh token from SDK (use this for API calls)

  // AUTHENTICATED FETCH (handles Bearer attach + automatic refresh-and-retry on 401)
  // Prefer this over manual fetch+token. See SDK auth.ts:968-1003.
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;

  // Re-fetch userinfo + tenants[] from the IdP without re-authenticating.
  // Used when an external signal (socket event, manual button) tells us our access changed.
  refreshUser: () => Promise<void>;

  // ACCESS CONTROL FUNCTION
  can: (options: { type: string }) => boolean;  // Check if user can access specific features

  // MESSAGING ROLES (mirror of the gateway's enforcement — for hiding UI only)
  access: Access;
  isGlobalAdmin: boolean;
  roleIn: (tenantId: string | null | undefined) => EffectiveRole | null;
  canIn: (capability: Capability, tenantId: string | null | undefined) => boolean;

  // USER INFO FUNCTION
  userInfo: () => { name: string; email?: string; [key: string]: any };  // Safe user info with defaults

  // COMMON
  login: () => void;
  logout: (options?: {
    /** default: end IdP session (recommended). Use `mode: 'local'` for app-only. */
    useOIDCLogout?: boolean;
    mode?: 'local' | 'idp';
    postLogoutRedirectUri?: string;
    state?: string;
  }) => void;
  loading: boolean;
  sdk: AuthSDK | null;
  error: string | null;
}

export const AuthContext = createContext<IAuthContext | undefined>(undefined);

type AuthProviderProps = {
  children: ReactNode;
  /**
   * What to do when the user hits the app without a session.
   * - `'login-required'` (default): redirect to the IdP login immediately.
   * - `'check-sso'`: silently check SSO; render children unauthenticated if no session.
   *
   * Different routes can choose different behavior — public pages opt for
   * `'check-sso'`, protected pages for `'login-required'`.
   */
  oidcInitWhenUnauthenticated?: 'check-sso' | 'login-required';
};

export const AuthProvider = ({
  children,
  oidcInitWhenUnauthenticated = 'login-required',
}: AuthProviderProps) => {
  const navigate = useNavigate();

  // OAuth authentication state (operators/admins)
  const [oauthUser, setOauthUser] = useState<any>(null);
  const [oauthToken, setOauthToken] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);

  // Common state
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const { t } = useTranslation();

  // Separate refs for SDK
  const oauthSDKRef = useRef<Auth | null>(null);
  const hasInitializedRef = useRef(false);
  const authenticatedRef = useRef(false);
  const verifyInFlightRef = useRef(false);
  // Guards against concurrent initAuth invocations. React 18 StrictMode double-mounts
  // effects in dev, which can cause two initAuth runs to race the OIDC code exchange
  // (first succeeds, second fails with invalid_grant because the auth code is single-use,
  // and the second's error path clobbers the first's success state). Set synchronously
  // at entry, cleared in finally so a failed init can retry.
  const initInFlightRef = useRef(false);

  const [debugAuth, setDebugAuth] = useState(false);

  useEffect(() => {
    authenticatedRef.current = authenticated;
  }, [authenticated]);

  // Update debug mode when state changes
  useEffect(() => {
    if (oauthSDKRef.current) {
      const auth = oauthSDKRef.current as { setDebug?: (v: boolean) => void };
      if (typeof auth.setDebug === 'function') {
        auth.setDebug(debugAuth);
      }
    }
  }, [debugAuth]);

  // Note: No interval needed for token refresh!
  // - SDK automatically refreshes tokens 60 seconds before expiry
  // - getToken() always calls sdk.getAccessToken() to get fresh token
  // - activeToken also calls sdk.getAccessToken() for fresh token
  // - Components should use getToken() for API calls (always fresh)
  // - State (oauthToken) is only used as fallback if SDK call fails

  useEffect(() => {
    const initAuth = async () => {
      // Prevent re-initialization if we've already successfully authenticated
      if (hasInitializedRef.current && authenticated) {
        if (debugAuth) authLog.debug('Auth already initialized, skipping...');
        return;
      }
      // Guard against concurrent invocations (StrictMode double-mount in dev).
      // Synchronous ref flip — the second invocation sees true and bails before
      // it ever touches the OIDC code exchange.
      if (initInFlightRef.current) {
        if (debugAuth) authLog.debug('initAuth already in flight, skipping concurrent invocation');
        return;
      }
      initInFlightRef.current = true;

      try {
        // Try OAuth Authentication
        // SDK handles all OAuth logic internally (sessionStorage check, hash check, redirect)
        if (debugAuth) authLog.debug('Checking OAuth Authentication...');

        // Use current path as redirect_uri so we land back where we started after auth.
        // Falls back to "/" only when the user is at the root. (messaging-front has no
        // /signin or /signup routes — that exclusion was leftover from a sibling app.)
        const currentPath = window.location.pathname;
        const callbackPath = currentPath !== "/" ? currentPath : "/";
        const redirectUri = window.location.origin + callbackPath;

        const config = {
          client_id: OIDC_CLIENT_ID,
          redirect_uri: redirectUri,
          authorization_endpoint: `${SSO_ORIGIN}/api/oidc/authorize`,
          token_endpoint: `${SSO_ORIGIN}/api/oidc/token`,
          userinfo_endpoint: `${SSO_ORIGIN}/api/oidc/userinfo`,
          scope: "openid profile email tenant roles",
          response_type: 'code' as const, // Explicitly use authorization code flow with PKCE
          debug: debugAuth,
        } as AuthConfig;

        const oauthSDK = new Auth(config);
        oauthSDKRef.current = oauthSDK;

        const isCallback = oauthSDK.isLoginCallback();
        // Use 'check-sso' for callbacks to avoid re-saving location and prevent redirect loops.
        // Otherwise honor the provider prop (`'login-required'` by default for messaging-front).
        const initType = isCallback ? 'check-sso' : oidcInitWhenUnauthenticated;

        if (debugAuth) {
          authLog.debug('OAuth initialization:', {
            isCallback,
            initType,
            url: window.location.href,
          });
        }

        const oauthResult = await oauthSDK.init({ type: initType });

        if (oauthResult?.error) {
          authLog.error('OAuth initialization error:', oauthResult.error);
          setAuthError(oauthResult.error);
          setAuthenticated(false);
          // Stop further login attempts when error occurs
          hasInitializedRef.current = true;
        } else if (oauthResult?.authenticated && oauthResult?.token) {
          // Validate the session against userinfo BEFORE flipping any auth state — otherwise
          // a brief inconsistent window exists where oauthToken is set but authenticated is
          // still false. Consumers gate on `authenticated` so it isn't user-visible, but
          // keeping the state transitions monotonic makes the AuthContext easier to reason
          // about and removes a "did the token leak through?" debugging dead-end.
          try {
            const userInfo = await oauthSDK.getUser();
            setOauthUser(userInfo);
            setOauthToken(oauthResult.token.access_token);
            setAuthenticated(true);
            setAuthError(null);
            hasInitializedRef.current = true;
            if (debugAuth) authLog.debug('OAuth user info loaded:', userInfo);
          } catch (userError) {
            authLog.error('Failed to fetch user info (session invalid):', userError);
            oauthSDK.logout();
            setOauthUser(null);
            setOauthToken(null);
            setAuthenticated(false);
            setAuthError(
              i18n.t('auth.sessionInvalid'),
            );
            hasInitializedRef.current = true;
          }
        }
      } catch (error) {
        authLog.error('Auth initialization failed:', error);
        setAuthError(error instanceof Error ? error.message : i18n.t('auth.failed'));
        setAuthenticated(false);
      } finally {
        setLoading(false);
        initInFlightRef.current = false;
      }
    };

    initAuth();
    // `authenticated` was intentionally dropped from the dep array — `hasInitializedRef`
    // already prevents re-init on the post-login render, and including it caused a
    // confusing extra effect run on every auth state change (short-circuited by the guard).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debugAuth, oidcInitWhenUnauthenticated]);

  /** Drop tokens + React state without IdP redirect (used when server says session is dead). */
  const clearLocalAuth = useCallback(() => {
    if (oauthSDKRef.current) {
      oauthSDKRef.current.logout();
    }
    setOauthUser(null);
    setOauthToken(null);
    setAuthenticated(false);
    setAuthError(null);
    hasInitializedRef.current = false;
  }, []);

  const verifySessionWithServer = useCallback(async () => {
    if (!authenticatedRef.current || verifyInFlightRef.current) return;
    const sdk = oauthSDKRef.current;
    if (!sdk) return;
    verifyInFlightRef.current = true;
    try {
      await sdk.getUser();
    } catch {
      clearLocalAuth();
      setAuthError(
        i18n.t('auth.sessionEnded'),
      );
    } finally {
      verifyInFlightRef.current = false;
    }
  }, [clearLocalAuth]);

  // After IdP logout in another tab/window, sessionStorage in this tab can still hold JWTs —
  // re-check userinfo when the tab becomes visible (debounced).
  //
  // Tab-leader optimisation (A4): with N tabs open, every visibility change fires a userinfo
  // call per tab. We use the Web Locks API to make sure only one tab at a time actually hits
  // the IdP. Other tabs skip silently; on their next visibility change they'll race for the
  // lock again. Falls back to plain verify if `navigator.locks` is unsupported (older Safari).
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const verifyWithTabLeader = async () => {
      const nav = navigator as Navigator & { locks?: LockManager };
      if (!nav.locks) {
        await verifySessionWithServer();
        return;
      }
      await nav.locks.request(
        'sso-userinfo-verify',
        { ifAvailable: true },
        async (lock) => {
          if (!lock) return; // Another tab is currently verifying — skip.
          await verifySessionWithServer();
        },
      );
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void verifyWithTabLeader(), 600);
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void verifyWithTabLeader();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [verifySessionWithServer]);

  const login = useCallback(async () => {
    if (!oauthSDKRef.current) {
      authLog.error('OAuth SDK not initialized');
      return;
    }

    // Post-login redirect is now handled automatically by the SDK (like Keycloak).
    // The SDK's login() method automatically saves the current location before redirecting
    // and automatically redirects back after successful authentication.

    // login() is now async (for PKCE code challenge generation)
    await oauthSDKRef.current.login();
  }, []);

  const logout = useCallback((options?: {
    useOIDCLogout?: boolean;
    mode?: 'local' | 'idp';
    postLogoutRedirectUri?: string;
    state?: string;
  }) => {
    // Default: end IdP session. Caller passes `mode: 'local'` (or `useOIDCLogout: false`) for local-only.
    const localOnly =
      options?.mode === 'local' || options?.useOIDCLogout === false;

    if (oauthSDKRef.current && !localOnly) {
      oauthSDKRef.current.logout({
        useOIDCLogout: true,
        postLogoutRedirectUri:
          options?.postLogoutRedirectUri ||
          `${window.location.origin}/`,
        state: options?.state,
      });
      return;
    }

    if (oauthSDKRef.current) {
      oauthSDKRef.current.logout();
    }

    setOauthUser(null);
    setOauthToken(null);
    setAuthenticated(false);
    setAuthError(null);
    hasInitializedRef.current = false;
    navigate('/', { replace: true });
  }, [navigate]);

  // Convenience properties for backward compatibility (OAuth takes precedence)
  const activeUser = useMemo(() => {
    return authenticated ? oauthUser : null;
  }, [authenticated, oauthUser]);

  // Get token from SDK to ensure we always have the latest refreshed token.
  // This ensures components always get the most up-to-date token after SDK auto-refresh.
  const activeToken = useMemo(() => {
    if (!authenticated || !oauthSDKRef.current) {
      return null;
    }
    // Always get token from SDK first to ensure we have the latest refreshed token
    try {
      const auth = oauthSDKRef.current as { getAccessToken?: () => string | null };
      const sdkToken = auth?.getAccessToken?.();
      if (sdkToken) {
        return sdkToken; // Always prefer SDK token (it's always fresh after auto-refresh)
      }
    } catch (error) {
      authLog.warn('Failed to get token from SDK, using state token:', error);
    }
    // Fallback to state token if SDK method not available or fails
    return oauthToken;
  }, [authenticated, oauthToken]);

  // Tenants from OIDC userinfo (scope `tenant`). The shape is preserved for
  // `useSocketOperatorRooms` which expects { id, name, slug }.
  const activeTenants = useMemo<
    Array<{ id: string; name: string; slug: string }> | undefined
  >(() => {
    const raw = (oauthUser as { tenants?: unknown } | null)?.tenants;
    if (!Array.isArray(raw)) return undefined;
    const out: Array<{ id: string; name: string; slug: string }> = [];
    for (const t of raw) {
      if (!t || typeof t !== 'object') continue;
      const r = t as Record<string, unknown>;
      const id = r.id != null ? String(r.id) : '';
      if (!id) continue;
      out.push({
        id,
        name: r.name != null ? String(r.name) : id,
        slug: r.slug != null ? String(r.slug) : id,
      });
    }
    return out.length > 0 ? out : undefined;
  }, [oauthUser]);

  // Access control function - flexible permission checking
  const can = useCallback((options: { type: string }) => {
    if (!authenticated || !oauthUser) {
      return false;
    }

    switch (options.type) {
      case 'admin':
      case 'admin-features':
      case 'settings':
        // SSO decides who is a global admin (userinfo `global_admin`).
        return accessFromUser(oauthUser).isGlobalAdmin;

      case 'menu':
        // Only OAuth authenticated users can access admin features
        return authenticated;

      case 'any':
      case 'view':
        // OAuth authenticated
        return authenticated;

      default:
        return false;
    }
  }, [authenticated, oauthUser]);

  // User info function - safely returns user info with defaults
  const userInfo = useCallback(() => {
    const currentUser = activeUser;
    if (!currentUser) {
      return {
        name: 'Guest',
        email: undefined,
      };
    }

    return {
      name: currentUser.name || currentUser.username || 'User',
      email: currentUser.email,
      ...currentUser, // Include any other properties from user object
    };
  }, [activeUser]);

  // Get fresh token from SDK - ALWAYS use this for API calls.
  // This ensures you always get the latest refreshed token.
  const getToken = useCallback((): string | null => {
    if (!authenticated || !oauthSDKRef.current) {
      return null;
    }
    try {
      const auth = oauthSDKRef.current as { getAccessToken?: () => string | null };
      const sdkToken = auth?.getAccessToken?.();
      return sdkToken || null;
    } catch (error) {
      authLog.warn('Failed to get token from SDK:', error);
      return oauthToken; // Fallback to state token
    }
  }, [authenticated, oauthToken]);

  /**
   * Authenticated fetch with auto-refresh-and-retry on 401 (delegates to SDK.apiFetch).
   * If the SDK isn't initialised, falls back to plain fetch (will likely 401 — caller's
   * responsibility to handle). Use this for every API call instead of raw fetch + Bearer.
   */
  const apiFetch = useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      const sdk = oauthSDKRef.current;
      const withTenant = withTenantHeader(url, init, config.apiServer);
      if (!sdk) return fetch(url, withTenant);
      return sdk.apiFetch(url, withTenant);
    },
    [],
  );

  /**
   * Pull a fresh userinfo from the IdP (re-reads `tenants[]`, `roles[]`, profile).
   * Updates state so consumers re-render with new tenants. No-op if not authenticated.
   * Caller should handle exceptions — usually we just log and keep stale state.
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    const sdk = oauthSDKRef.current;
    if (!sdk || !authenticatedRef.current) return;
    try {
      const fresh = await sdk.getUser();
      setOauthUser(fresh);
    } catch (err) {
      authLog.warn('refreshUser failed:', err);
    }
  }, []);

  const access = useMemo(() => accessFromUser(oauthUser), [oauthUser]);
  const roleInTenant = useCallback((tenantId: string | null | undefined) => roleIn(access, tenantId), [access]);
  const canIn = useCallback(
    (capability: Capability, tenantId: string | null | undefined) => canAccess(access, capability, tenantId),
    [access],
  );

  const pack = useMemo(() => ({
    // Distinct OAuth properties
    oauthUser,
    oauthToken,
    authenticated,

    // Convenience properties (for components - high-level API)
    // activeToken already gets fresh token from SDK, so use it directly
    user: activeUser,
    token: activeToken, // This always gets the latest token from SDK (for backward compatibility)
    isOAuth: authenticated,
    tenants: activeTenants,

    // Token function - BEST PRACTICE: Always use this for API calls
    getToken, // Always gets fresh token from SDK

    // Authenticated fetch (Bearer + 401 retry)
    apiFetch,

    // Re-fetch userinfo (refresh tenants[] / roles[])
    refreshUser,

    // Access control & user info functions
    can,
    access,
    isGlobalAdmin: access.isGlobalAdmin,
    roleIn: roleInTenant,
    canIn,
    userInfo,

    // Common properties
    login,
    logout,
    loading,
    sdk: oauthSDKRef.current,
    error: authError,
  }), [
    oauthUser,
    oauthToken,
    authenticated,
    activeUser,
    activeToken,
    activeTenants,
    getToken,
    apiFetch,
    refreshUser,
    can,
    access,
    roleInTenant,
    canIn,
    userInfo,
    login,
    logout,
    loading,
    authError,
  ]);

  return (
    <AuthContext.Provider value={pack}>
      {import.meta.env.DEV && (
        <button
          type="button"
          onClick={() => setDebugAuth((d) => !d)}
          style={{
            position: 'fixed',
            bottom: 8,
            right: 8,
            zIndex: 99999,
            fontSize: 11,
            opacity: 0.7,
          }}
          title="Toggle OAuth SDK debug"
        >
          OAuth debug {debugAuth ? 'on' : 'off'}
        </button>
      )}
      {authError && (
        <div className="bg-danger-soft border border-danger text-danger px-4 py-3 rounded mb-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <strong className="font-bold">{t('auth.errorTitle')}</strong>
              <span className="block sm:inline ml-1">{authError}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  setAuthError(null);
                  void login();
                }}
                className="bg-danger hover:opacity-90 text-on-primary text-xs sm:text-sm font-medium px-3 py-1.5 rounded"
              >
                {t('auth.signIn')}
              </button>
              <button
                type="button"
                onClick={() => setAuthError(null)}
                aria-label={t('auth.dismiss')}
                className="text-danger hover:text-danger"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      {
        loading ? (
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted">{t('auth.authenticating')}</p>
            </div>
          </div>
        ) : (
          children
        )
      }

    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
